import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import Swal from "sweetalert2";
import { LIMIT_DATA_PER_PAGE } from "utils/constants";
import { buildQueryString } from "utils/queryHelpers";
import { formatDateToLocal } from "utils/utils";

const ScheduleContext = createContext();

export const ScheduleProvider = ({ children }) => {
  const [schedules, setSchedules] = useState({
    data: [],
    pagination: null,
    statistics: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const paramStart = params.get("startDate");
    const paramEnd = params.get("endDate");
    
    const now = new Date();
    const currentDay = now.getDate();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Auto deteksi ganti bulan di tgl 26
    let defaultStart, defaultEnd;
    if (currentDay >= 26) {
      defaultStart = new Date(currentYear, currentMonth, 26);
      defaultEnd = new Date(currentYear, currentMonth + 1, 25);
    } else {
      defaultStart = new Date(currentYear, currentMonth - 1, 26);
      defaultEnd = new Date(currentYear, currentMonth, 25);
    }

    return {
      search: params.get("search") || "",
      currentPage: parseInt(params.get("page")) || 1,
      perPage: 100,
      startDate: paramStart ? new Date(paramStart) : defaultStart,
      endDate: paramEnd ? new Date(paramEnd) : defaultEnd,
    };
  });

  // Sync filterOptions to URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (filterOptions.startDate) params.set("startDate", formatDateToLocal(filterOptions.startDate));
    if (filterOptions.endDate) params.set("endDate", formatDateToLocal(filterOptions.endDate));
    if (filterOptions.search) params.set("search", filterOptions.search);
    else params.delete("search");
    if (filterOptions.currentPage !== 1) params.set("page", filterOptions.currentPage);
    else params.delete("page");

    const newRelativePathQuery = window.location.pathname + "?" + params.toString();
    window.history.replaceState(null, "", newRelativePathQuery);
  }, [filterOptions]);

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    all: 1,
    include: "user,shift,pattern",
    "filter[date_range]": [
      filterOptions.startDate ? formatDateToLocal(filterOptions.startDate) : null,
      filterOptions.endDate ? formatDateToLocal(filterOptions.endDate) : null
    ].filter(Boolean),
  });

  // Handle search input
  const handleSearch = (e) => {
    if (e.key === "Enter") {
      if (!e.target.value.trim()) {
        // Tampilkan toast jika input kosong
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "warning",
          title: "Search input cannot be empty!",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      console.log("search", e.target.value);
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  // Fetch schedules from API
  const fetchSchedules = useCallback(async () => {
    setSchedules((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/ahr/schedules${queryString}`);
      if (response.success) {
        setSchedules({
          data: response.data,
          pagination: response.pagination,
          statistics: response.statistics,
          error: null,
          loading: false,
        });
      } else {
        console.log("response else", response);
        // Jika API error, tampilkan alert dengan tombol OK
        Swal.fire({
          icon: "error",
          title: "Search Error",
          text: response.message || "Something went wrong during the search.",
          confirmButtonText: "OK",
        }).then(() => {
          // Reload halaman setelah klik OK
          window.location.reload();
        });
      }
    } catch (error) {
      // Tangani error lain seperti koneksi atau server error
      console.log("Failed to fetch schedules: ", error);
      Swal.fire({
        icon: "error",
        title: "Search Error",
        text: error.message || "Failed to fetch schedules.",
        confirmButtonText: "OK",
      }).then(() => {
        // Reload halaman setelah klik OK
        window.location.reload();
      });
    }
  }, [queryString]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = schedules.pagination?.last_page || 1;

  const PaginationComponent = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5, // Optional: number of visible pages in pagination (default is 5)
  });

  return (
    <ScheduleContext.Provider
      value={{
        schedules,
        filterOptions,
        setFilterOptions,
        handleSearch,
        refetch: fetchSchedules,
        PaginationComponent,
      }}
    >
      {children}
    </ScheduleContext.Provider>
  );
};

export const useSchedule = () => {
  const context = useContext(ScheduleContext);

  if (!context) {
    throw new Error("useSchedule must be used within ScheduleProvider");
  }

  return context;
};
